import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Clock, DollarSign, Briefcase, Users, CheckCircle, Star, Sparkles, Phone, Mail, Building2, User, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [job, setJob] = useState(null);
  const [matchedTrainers, setMatchedTrainers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);

  const fetchJob = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}`);
      setJob(response.data);
    } catch (error) {
      toast.error('Failed to load job details');
      navigate('/search?type=jobs');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchMatchedTrainers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}/matched-trainers`);
      setMatchedTrainers(response.data);
    } catch (error) {
      console.error('Failed to fetch matched trainers:', error);
    }
  }, [id]);

  const fetchApplications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/applications/job/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    }
  }, [id, token]);

  const checkIfApplied = useCallback(async () => {
    if (!user || !token) return;
    try {
      const trainerRes = await axios.get(`${API}/trainers/user/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const trainerId = trainerRes.data.id;
      const appsRes = await axios.get(`${API}/applications/trainer/${trainerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasApplied(appsRes.data.some(app => app.job_id === id));
    } catch (error) {
      console.error('Failed to check application status:', error);
    }
  }, [id, user, token]);

  useEffect(() => {
    fetchJob();
    if (user?.user_type === 'corporate') {
      fetchMatchedTrainers();
      fetchApplications();
    }
    if (user?.user_type === 'trainer') {
      checkIfApplied();
    }
  }, [fetchJob, fetchMatchedTrainers, fetchApplications, checkIfApplied, user]);

  const handleApply = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.user_type !== 'trainer') {
      toast.error('Only trainers can apply to jobs');
      return;
    }
    
    setApplying(true);
    try {
      await axios.post(`${API}/applications`, {
        job_id: id,
        cover_note: ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application submitted successfully!');
      setHasApplied(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!job) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="job-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <Link to="/" className="flex items-center gap-2" data-testid="logo">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <span className="font-outfit font-bold text-xl text-slate-900 hidden sm:block">Training Solutions</span>
              </Link>
            </div>

            {user?.user_type === 'trainer' && (
              <div className="flex items-center gap-2">
                {job.corporate_user_id && (
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/messages?with=${job.corporate_user_id}&name=${encodeURIComponent(job.corporate_name || 'Company')}`)}
                    className="rounded-full px-4"
                    data-testid="message-company-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Message
                  </Button>
                )}
                <Button
                  onClick={handleApply}
                  disabled={applying || hasApplied || job.status !== 'open'}
                  className={`rounded-full px-6 ${
                    hasApplied
                      ? 'bg-emerald-600 hover:bg-emerald-600'
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white`}
                  data-testid="apply-btn"
                >
                {hasApplied ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Applied
                  </>
                ) : applying ? (
                  'Applying...'
                ) : (
                  'Apply Now'
                )}
              </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Job Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-3 py-1 text-sm rounded-full font-medium ${
                  job.status === 'open'
                    ? 'bg-emerald-50 text-emerald-700'
                    : job.status === 'filled'
                    ? 'bg-blue-50 text-blue-700'
                    : job.status === 'completed'
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-slate-100 text-slate-500'
                }`}>
                  {job.status === 'completed' ? 'Completed' : job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                </span>
                {job.training_status === 'completed' && (
                  <span className="px-3 py-1 text-sm rounded-full font-semibold bg-emerald-600 text-white flex items-center gap-1" data-testid="training-complete-badge">
                    <CheckCircle className="w-4 h-4" /> Training Complete
                  </span>
                )}
                {job.training_status === 'in_progress' && (
                  <span className="px-3 py-1 text-sm rounded-full font-medium bg-blue-100 text-blue-700" data-testid="training-in-progress-badge">
                    Training In Progress
                  </span>
                )}
                {job.training_status === 'feedback_pending' && (
                  <span className="px-3 py-1 text-sm rounded-full font-medium bg-purple-100 text-purple-700" data-testid="feedback-pending-badge">
                    Feedback Collection
                  </span>
                )}
                <span className="text-slate-400">•</span>
                <span className="text-slate-500">{job.applications_count} applications</span>
              </div>

              <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-4" data-testid="job-title">
                {job.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-slate-500">
                <span className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  {job.corporate_name}
                </span>
                {job.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {job.location}
                  </span>
                )}
                {job.training_mode && (
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                    job.training_mode === 'on-site' ? 'bg-emerald-50 text-emerald-700' :
                    job.training_mode === 'online' ? 'bg-blue-50 text-blue-700' :
                    'bg-purple-50 text-purple-700'
                  }`}>
                    {job.training_mode}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {job.duration_hours} hours
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(job.start_date).toLocaleDateString()} - {new Date(job.end_date).toLocaleDateString()}
                </span>
                {job.training_time && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {job.training_time}
                  </span>
                )}
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 text-center md:text-right">
              <div className="text-sm text-slate-500 mb-1">Budget</div>
              <div className="font-outfit text-3xl font-bold text-slate-900">
                {job.budget_currency === 'USD' ? '$' : job.budget_currency === 'EUR' ? '€' : job.budget_currency === 'GBP' ? '£' : '₹'}{job.budget?.toLocaleString()}
                <span className="text-sm font-normal text-slate-500 ml-1">{job.budget_currency || 'INR'}</span>
              </div>
              {job.rate_per_hour > 0 && (
                <div className="text-sm text-slate-500 mt-1">{job.budget_currency === 'USD' ? '$' : '₹'}{job.rate_per_hour?.toLocaleString()}/hr per trainer</div>
              )}
              {job.num_trainers > 1 && (
                <div className="text-sm text-blue-600 mt-1">{job.num_trainers} trainers required</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Description */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">Description</h3>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                {job.description}
              </p>
            </div>

            {/* Venue & Contact Details */}
            {(job.venue_address || job.contact_person || job.contact_phone) && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Venue & Contact Details
                </h3>
                <div className="space-y-4">
                  {job.venue_address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm text-slate-500 font-medium">Venue Address</div>
                        <div className="text-slate-900" data-testid="venue-address-display">{job.venue_address}</div>
                      </div>
                    </div>
                  )}
                  {job.contact_person && (
                    <div className="flex items-start gap-3">
                      <User className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm text-slate-500 font-medium">Contact Person</div>
                        <div className="text-slate-900" data-testid="contact-person-display">{job.contact_person}</div>
                      </div>
                    </div>
                  )}
                  {job.contact_phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm text-slate-500 font-medium">Contact Number</div>
                        <a href={`tel:${job.contact_phone}`} className="text-blue-600 hover:underline" data-testid="contact-phone-display">{job.contact_phone}</a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Training Requirements */}
            {(job.num_trainers > 1 || (job.trainer_roles && job.trainer_roles.length > 0) || job.rate_per_hour > 0) && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Trainer Requirements
                </h3>
                <div className="space-y-3">
                  {job.num_trainers > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">Trainers Required</span>
                      <span className="font-medium text-slate-900">{job.num_trainers}</span>
                    </div>
                  )}
                  {job.rate_per_hour > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">Rate per Hour/Trainer</span>
                      <span className="font-medium text-slate-900">₹{job.rate_per_hour?.toLocaleString()}</span>
                    </div>
                  )}
                  {job.trainer_roles && job.trainer_roles.length > 0 && (
                    <div className="pt-2">
                      <span className="text-sm text-slate-500 mb-2 block">Trainer Roles</span>
                      <div className="flex flex-wrap gap-2">
                        {job.trainer_roles.map((role, i) => (
                          <span key={i} className="px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full">{role}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Corporate Contact */}
            {(job.corporate_email || job.corporate_phone) && (
              <div className="bg-blue-50 rounded-xl border border-blue-100 p-6">
                <h3 className="font-outfit font-semibold text-lg text-blue-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                  Company Contact
                </h3>
                <div className="space-y-3">
                  {job.corporate_email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <a href={`mailto:${job.corporate_email}`} className="text-blue-700 hover:underline" data-testid="corporate-email-display">{job.corporate_email}</a>
                    </div>
                  )}
                  {job.corporate_phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <a href={`tel:${job.corporate_phone}`} className="text-blue-700 hover:underline" data-testid="corporate-phone-display">{job.corporate_phone}</a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Training Feedback Summary */}
            {job.training_status === 'completed' && job.feedback_count > 0 && (
              <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-6" data-testid="training-feedback-summary">
                <h3 className="font-outfit font-semibold text-lg text-emerald-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  Training Completed
                </h3>
                <p className="text-emerald-700">
                  This training has been successfully completed with <strong>{job.feedback_count} participant feedback(s)</strong> received.
                </p>
              </div>
            )}

            {/* Skills Required */}
            {job.skills_required?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">Required Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {job.skills_required.map(skill => (
                    <span key={skill} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Qualifications Required */}
            {job.qualifications_required?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">Required Qualifications</h3>
                <ul className="space-y-3">
                  {job.qualifications_required.map((qual, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{qual}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Matched Trainers - Only visible to job poster - PROMINENT SECTION */}
            {user?.user_type === 'corporate' && matchedTrainers.length > 0 && (
              <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-xl border-2 border-emerald-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-outfit font-semibold text-lg text-slate-900">
                    Suggested Trainers ({matchedTrainers.length})
                  </h3>
                </div>
                <p className="text-slate-600 text-sm mb-4">
                  These trainers match your requirements based on skills, experience, and availability.
                </p>
                <div className="space-y-3">
                  {matchedTrainers.map(trainer => (
                    <div
                      key={trainer.id}
                      className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:shadow-md hover:border-emerald-300 cursor-pointer transition-all"
                      onClick={() => navigate(`/trainer/${trainer.id}`)}
                      data-testid={`matched-trainer-${trainer.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                          {trainer.name?.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{trainer.name}</div>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {trainer.location || 'Remote'}
                            </span>
                            <span>{trainer.experience_years || 0}+ yrs exp</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-slate-900">₹{trainer.hourly_rate}/hr</div>
                        <div className="flex items-center gap-1 text-sm">
                          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                          <span className="text-slate-600">{trainer.rating?.toFixed(1) || '0.0'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No matched trainers message for corporates */}
            {user?.user_type === 'corporate' && matchedTrainers.length === 0 && job?.status === 'open' && (
              <div className="bg-amber-50 rounded-xl border border-amber-200 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-amber-900">No matching trainers found</h3>
                </div>
                <p className="text-amber-700 text-sm">
                  No trainers currently match all your requirements. Try broadening your criteria or wait for trainers to apply.
                </p>
              </div>
            )}

            {/* Applications - Only visible to job poster */}
            {user?.user_type === 'corporate' && applications.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">
                  Applications ({applications.length})
                </h3>
                <div className="space-y-4">
                  {applications.map(app => (
                    <div
                      key={app.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center text-white font-bold">
                          {app.trainer_name?.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{app.trainer_name}</div>
                          <div className="text-sm text-slate-500">
                            Applied {new Date(app.applied_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          app.status === 'pending'
                            ? 'bg-amber-50 text-amber-700'
                            : app.status === 'accepted'
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-red-50 text-red-700'
                        }`}>
                          {app.status}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/trainer/${app.trainer_id}`)}
                        >
                          View Profile
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Job Details Card */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">Requirement Details</h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-slate-500">Training Type</span>
                  <span className="font-medium text-slate-900">{job.training_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Industry</span>
                  <span className="font-medium text-slate-900">{job.industry}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Duration</span>
                  <span className="font-medium text-slate-900">{job.duration_hours} hours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Start Date</span>
                  <span className="font-medium text-slate-900">
                    {new Date(job.start_date).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">End Date</span>
                  <span className="font-medium text-slate-900">
                    {new Date(job.end_date).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Posted</span>
                  <span className="font-medium text-slate-900">
                    {new Date(job.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            {/* CTA Card */}
            {user?.user_type === 'trainer' && job.status === 'open' && !hasApplied && (
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                <h3 className="font-outfit font-semibold text-lg text-blue-900 mb-2">
                  Interested in this opportunity?
                </h3>
                <p className="text-blue-700 text-sm mb-4">
                  Apply now and showcase your expertise to the company.
                </p>
                <Button
                  onClick={handleApply}
                  disabled={applying}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="sidebar-apply-btn"
                >
                  {applying ? 'Applying...' : 'Apply Now'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
